import { useState } from 'react';
// mantine
import {
    Button,
    Slider,
    TagsInput,
    Textarea,
    TextInput,
    UnstyledButton
} from '@mantine/core';
import { useFocusTrap } from '@mantine/hooks';

// utils
import { useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { processFormData } from '@newo/toolkit/bff';
import type { ITopic, ITopicData } from '@newo/api/gateway';
import validationSchema from '@/pages/AKB/components/TopicForm/TopicForm.schema.ts';
import {
    useCreateTopicMutation,
    useUpdateTopicMutation
} from '@/services/akb/akb.queries.ts';

interface TopicFormProps {
    personaId: string;
    topic?: ITopic;
    onClose?: () => void;
    isCopy?: boolean;
}

const DEFAULT_CONFIDENCE = 100;

const TopicForm = ({
    personaId,
    topic,
    onClose,
    isCopy = false
}: TopicFormProps) => {
    const focusTrapRef = useFocusTrap(isCopy);
    const queryClient = useQueryClient();
    const [searchValue, setSearchValue] = useState('');
    const mutationAdd = useCreateTopicMutation();
    const mutationUpdate = useUpdateTopicMutation();

    const initialValues: ITopicData = {
        topic_name: topic?.topic_name ?? '',
        persona_id: personaId,
        topic_summary: topic?.topic_summary ?? '',
        topic_facts: topic?.topic_facts ?? [],
        confidence: topic?.confidence ?? DEFAULT_CONFIDENCE,
        source: isCopy ? '' : (topic?.source ?? ''),
        labels: topic?.labels ?? []
    };
    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit(formData) {
            const data = processFormData({
                ...formData,
                labels: searchValue
                    ? formData.labels.concat(searchValue)
                    : formData.labels
            });
            if (topic && !isCopy) {
                mutationUpdate.mutate(
                    {
                        topicId: topic.topic_id,
                        data
                    },
                    {
                        onSuccess() {
                            queryClient.invalidateQueries({
                                queryKey: ['akb/topics']
                            });
                            onClose?.();
                        },
                        onSettled() {
                            formik.setSubmitting(false);
                        }
                    }
                );
                return;
            }
            mutationAdd.mutate(data, {
                onSuccess() {
                    queryClient.invalidateQueries({
                        queryKey: ['akb/topics']
                    });
                    onClose?.();
                },
                onSettled() {
                    formik.setSubmitting(false);
                }
            });
        }
    });

    const handleCancel = () => {
        onClose?.();
    };

    const handleFactsChange = (
        event: React.ChangeEvent<HTMLTextAreaElement>
    ) => {
        formik.setFieldValue('topic_facts', event.target.value.split('\n'));
    };

    const handleSliderChange = (value: number) => {
        formik.setFieldValue('confidence', value);
    };

    const handleLabelsChange = (value: string[]) => {
        formik.setFieldValue('labels', value);
    };

    const handleFactsBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
        formik.handleBlur(event);

        const facts = event.target.value
            .split('\n')
            .map((fact) => fact.trim())
            .filter(Boolean);
        formik.setFieldValue('topic_facts', facts);
    };

    return (
        <>
            {topic && !isCopy ? (
                <>
                    <div className="text-xs text-gray-6">
                        Created at: {topic.created_at}
                    </div>
                    <div className="text-xs text-gray-6">
                        Updated at: {topic.updated_at}
                    </div>
                </>
            ) : undefined}
            <form
                autoComplete="off"
                className="flex flex-col gap-3"
                onSubmit={formik.handleSubmit}
            >
                <TextInput
                    error={
                        formik.touched.topic_name
                            ? formik.errors.topic_name
                            : undefined
                    }
                    label="Name"
                    name="topic_name"
                    placeholder="Topic name"
                    size="sm"
                    value={formik.values.topic_name}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                />

                <Textarea
                    autosize
                    error={
                        formik.touched.topic_summary
                            ? formik.errors.topic_summary
                            : undefined
                    }
                    label="Summary"
                    maxRows={40}
                    minRows={15}
                    name="topic_summary"
                    placeholder="Topic summary"
                    size="sm"
                    value={formik.values.topic_summary}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                />

                <Textarea
                    autosize
                    error={
                        formik.touched.topic_facts
                            ? formik.errors.topic_facts
                            : undefined
                    }
                    label="Facts"
                    maxRows={10}
                    minRows={15}
                    name="topic_facts"
                    placeholder="Topic facts"
                    size="sm"
                    value={formik.values.topic_facts.join('\n')}
                    onBlur={handleFactsBlur}
                    onChange={handleFactsChange}
                />

                <div className="mb-2 mt-1">
                    <div className="text-xs">Confidence</div>
                    <Slider
                        marks={[
                            { value: 0, label: '0' },
                            { value: 100, label: '100' }
                        ]}
                        max={100}
                        min={0}
                        size="md"
                        value={formik.values.confidence}
                        onChange={handleSliderChange}
                    />
                </div>

                <TextInput
                    data-autofocus={isCopy}
                    error={
                        formik.touched.source && formik.values.source
                            ? formik.errors.source
                            : undefined
                    }
                    label="Source"
                    name="source"
                    placeholder="Source"
                    ref={focusTrapRef}
                    size="sm"
                    value={formik.values.source}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                />

                <TagsInput
                    description="Press Enter to submit a label"
                    label="Labels"
                    placeholder="Enter label"
                    searchValue={searchValue}
                    size="sm"
                    value={formik.values.labels}
                    onChange={handleLabelsChange}
                    onSearchChange={setSearchValue}
                />

                <div className="flex justify-between gap-2">
                    <UnstyledButton
                        className="text-xs text-gray-6"
                        onClick={handleCancel}
                    >
                        Cancel
                    </UnstyledButton>
                    <Button
                        disabled={
                            (!formik.dirty && !searchValue) ||
                            !formik.isValid ||
                            formik.isSubmitting
                        }
                        size="xs"
                        type="submit"
                    >
                        {!!topic && !isCopy && 'Update'}
                        {isCopy ? 'Submit' : undefined}
                        {!topic && 'Create'}
                    </Button>
                </div>
            </form>
        </>
    );
};

export default TopicForm;
